import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Empty, Rows } from "@/components/ui-helpers";
import { publicUrl, assetUrl } from "@/lib/forms";
import { fmtRel } from "@/lib/fmt";
import { Plus, Copy, ExternalLink, Files, MessageSquareText, Flag, Clock3, ArrowUpRight, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function Spark({ data, color }) {
  const max = Math.max(1, ...data);
  return (
    <div className="flex items-end gap-[3px] h-9" title="Responses, last 14 days">
      {data.map((n, i) => <div key={i} className="flex-1 rounded-sm" style={{ height: `${Math.max(6, (n / max) * 100)}%`, background: n ? color : "#ffffff14" }} />)}
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 flex items-center gap-1"><Icon size={10} />{label}</div>
      <div className="text-lg font-semibold tabular-nums leading-tight">{value}</div>
    </div>
  );
}

function FormCard({ f, i, onOpen, onCopy, onDup }) {
  const th = f.theme || {};
  const accent = f.accent && f.accent !== "#ffffff" ? f.accent : "#e5e5e5";
  const total = f.responses + (f.partials || 0);
  const rate = total ? Math.round((f.responses / total) * 100) : null;
  const dq = (f.settings?.endings || []).filter((e) => e.outcome === "disqualified" || e.route === "dq").length;
  return (
    <div onClick={onOpen} data-testid={`form-card-${f.slug}`} style={{ animationDelay: `${i * 60}ms` }}
      className="group panel overflow-hidden cursor-pointer transition-[transform,border-color] duration-200 hover:-translate-y-0.5 hover:border-neutral-500 animate-in fade-in slide-in-from-bottom-2 fill-mode-both">
      <div className="relative h-28 p-4 flex flex-col justify-between" style={{ background: th.bg_color || "#0a0a0a", color: th.text_color || "#fff" }}>
        {th.bg_image_url && <img src={assetUrl(th.bg_image_url)} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 1 - (th.bg_overlay ?? 0.5) }} />}
        <div className="relative flex items-center justify-between">
          {th.logo_url ? <img src={assetUrl(th.logo_url)} alt="" className="h-5 object-contain" /> : <span />}
          <span className={`pill text-[10px] backdrop-blur ${f.status === "published" ? "pill-ok" : ""}`} data-testid={`form-status-${f.slug}`}>{f.status}</span>
        </div>
        <div className="relative text-base font-semibold leading-snug line-clamp-2" style={{ fontFamily: th.font || undefined }}>{f.title}</div>
        <ArrowUpRight size={16} className="absolute right-3 bottom-3 opacity-0 group-hover:opacity-70 transition-opacity" />
      </div>
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <Stat icon={MessageSquareText} label="Responses" value={f.responses} testid={`form-responses-${f.slug}`} />
          <Stat icon={Clock3} label="Partials" value={f.partials || 0} testid={`form-partials-${f.slug}`} />
          <Stat icon={Flag} label="Completion" value={rate == null ? "—" : `${rate}%`} testid={`form-completion-${f.slug}`} />
        </div>
        <Spark data={f.spark || []} color={accent} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500">
          <span>{f.questions} questions</span><span>{(f.settings?.endings || []).length} endings{dq ? ` · ${dq} DQ` : ""}</span>
          <span>{f.last_response ? `last ${fmtRel(f.last_response)}` : "no responses yet"}</span>
        </div>
        <div className="flex items-center gap-1 border-t border-[var(--border)] pt-3">
          <span className="text-[11px] text-neutral-500 font-mono truncate flex-1">{publicUrl(f.slug).replace(/^https?:\/\//, "")}</span>
          <button className="btn-ghost btn p-1.5" title="Copy link" onClick={onCopy} data-testid={`form-copy-${f.slug}`}><Copy size={13} /></button>
          <a className="btn-ghost btn p-1.5" title="Open" href={publicUrl(f.slug)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`form-open-${f.slug}`}><ExternalLink size={13} /></a>
          <button className="btn-ghost btn p-1.5" title="Duplicate" onClick={onDup} data-testid={`form-dup-${f.slug}`}><Files size={13} /></button>
        </div>
      </div>
    </div>
  );
}

function ArchivedForms({ onRestored }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/archived-forms").then((r) => setRows(r.data.rows)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const restore = async (f, publish) => {
    try {
      const { data } = await api.post(`/forms/${f._id}/restore`, { publish });
      toast.success(`“${f.title}” restored as ${data.status}`); load(); onRestored();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't restore"); }
  };
  if (!rows) return <Rows n={2} />;
  if (!rows.length) return <div className="text-sm text-neutral-500" data-testid="archived-empty">No archived forms.</div>;
  return (
    <div className="panel divide-y divide-[var(--border)]" data-testid="archived-forms">
      {rows.map((f) => (
        <div key={f._id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`archived-form-${f._id}`}>
          <div className="flex-1 min-w-[200px]">
            <div className="text-sm font-medium">{f.title}</div>
            <div className="text-[11px] text-neutral-500 font-mono">/{f.slug} · archived {f.archived_at ? fmtRel(f.archived_at) : "earlier"} · {f.responses} responses{f.prev_status ? ` · was ${f.prev_status}` : ""}</div>
            {f.slug_taken && <div className="text-[11px] text-[var(--warn)]">Another form now uses this link, so change that form's link before restoring this one.</div>}
          </div>
          <button className="btn btn-primary text-xs" onClick={() => restore(f, true)} data-testid={`restore-publish-${f._id}`}><RotateCcw size={12} />Restore &amp; publish</button>
          <button className="btn text-xs" onClick={() => restore(f, false)} data-testid={`restore-draft-${f._id}`}>Restore as draft</button>
        </div>
      ))}
    </div>
  );
}

export default function Forms() {
  const [rows, setRows] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const nav = useNavigate();
  const load = () => api.get("/forms").then((r) => setRows(r.data.rows));
  useEffect(() => { load(); }, []);
  const create = async () => {
    const { data } = await api.post("/forms", { title: "Untitled form", blocks: [] });
    nav(`/forms/${data._id}`);
  };
  const duplicate = async (e, id) => { e.stopPropagation(); await api.post(`/forms/${id}/duplicate`); toast.success("Form duplicated"); load(); };
  const copy = (e, slug) => { e.stopPropagation(); navigator.clipboard?.writeText(publicUrl(slug)); toast.success("Link copied"); };
  return (
    <div data-testid="forms-page">
      <PageHeader title="Forms" sub="Typeform replacement · every submission lands in the CRM"
        right={<div className="flex gap-2">
          <button className={`btn ${showArchived ? "btn-primary" : ""}`} onClick={() => setShowArchived(!showArchived)} data-testid="archived-forms-toggle"><Archive size={14} />{showArchived ? "Back to forms" : "Archived"}</button>
          <button className="btn btn-primary" onClick={create} data-testid="new-form-btn"><Plus size={14} />New form</button>
        </div>} />
      <div className="p-4 md:p-6">
        {showArchived ? <ArchivedForms onRestored={load} /> : !rows ? <Rows n={4} /> : rows.length === 0 ? <Empty label="No forms yet" icon={Files} action="Create your first form" onAction={create} /> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="forms-grid">
            {rows.map((f, i) => <FormCard key={f._id} f={f} i={i} onOpen={() => nav(`/forms/${f._id}`)} onCopy={(e) => copy(e, f.slug)} onDup={(e) => duplicate(e, f._id)} />)}
            <button onClick={create} className="panel border-dashed min-h-[280px] flex flex-col items-center justify-center gap-2 text-neutral-500 hover:text-white hover:border-neutral-500 transition-colors" data-testid="new-form-card">
              <Plus size={20} /><span className="text-sm">New form</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
